// @mui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { translateKey } from 'src/locales/translate';


// ----------------------------------------------------------------------

type MicrophoneAndCameraPermissionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function MicrophoneAndCameraPermissionsModal({
  isOpen,
  onClose,
}: MicrophoneAndCameraPermissionsModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const requestPermissions = async () => {
    try {
      setIsRequestingPermissions(true);
      setPermissionDenied(false);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      stream.getTracks().forEach((track) => track.stop());

      enqueueSnackbar(translateKey("hardcoded.uprawnienia_zostaly_przyznane_pomyslnie"), {
        variant: 'success',
      });

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Permission request failed:', error);
      setPermissionDenied(true);
      enqueueSnackbar(
        translateKey("hardcoded.nie_udalo_sie_uzyskac_uprawnien_wlacz_uprawnienia_w"),
        {
          variant: 'error',
        },
      );
    } finally {
      setIsRequestingPermissions(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => { }} disableEscapeKeyDown>
      <DialogTitle>{translateKey("hardcoded.dostep_do_kamery_i_mikrofonu")}</DialogTitle>

      <DialogContent sx={{ maxWidth: '600px' }}>
        <DialogContentText id="alert-dialog-description">{translateKey("hardcoded.abysmy_mogli_zapewnic_ci_najwyzsza_jakosc_obslugi_podczas")}<br />
          <br />
          <strong>{translateKey("hardcoded.jesli_uprawnienia_zostaly_wczesniej_zablokowane_wlacz_je_w")}</strong>
          <br />
          <br />{translateKey("hardcoded.ponizej_znajdziesz_linki_do_instrukcji_ktore_pomoga_ci")}</DialogContentText>
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography color="text.secondary">Google Chrome:</Typography>
            <Link
              component="a"
              target="_blank"
              href="https://support.google.com/chrome/answer/2693767?sjid=7712301754244710999-EU"
            >
              Komputer
            </Link>
            <Link
              component="a"
              target="_blank"
              href="https://support.google.com/chrome/answer/2693767?sjid=7712301754244710999-EU&co=GENIE.Platform%3DiOS&oco=1"
            >
              iOS
            </Link>
            <Link
              component="a"
              target="_blank"
              href="https://support.google.com/chrome/answer/2693767?sjid=7712301754244710999-EU&co=GENIE.Platform%3DAndroid&oco=1"
            >
              Android
            </Link>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography color="text.secondary">Safari:</Typography>
            <Link
              component="a"
              target="_blank"
              href="https://support.apple.com/pl-pl/guide/safari/ibrw7f78f7fe/mac"
            >
              Komputer
            </Link>
            <Link
              component="a"
              target="_blank"
              href="https://support.apple.com/pl-pl/guide/iphone/iph168c4bbd5/ios"
            >
              iOS
            </Link>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography color="text.secondary">Mozilla Firefox:</Typography>
            <Link
              component="a"
              target="_blank"
              href="https://support.mozilla.org/pl/kb/zarzadzanie-uprawnieniami-kamery-i-mikrofonu-firefox"
            >
              Komputer
            </Link>
            <Link
              component="a"
              target="_blank"
              href="https://support.mozilla.org/pl/kb/zarzadzanie-uprawnieniami-kamery-i-mikrofonu-firefox#w_uzycie-menu-ustawien-urzadzenia-z-systemem-ios-w-celu-zmiany-uprawnien-kamery-i-mikrofonu"
            >
              iOS
            </Link>
            <Link
              component="a"
              target="_blank"
              href="https://support.mozilla.org/pl/kb/przyznanie-dostepu-do-kamery-firefox-android"
            >
              Android
            </Link>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography color="text.secondary">Microsoft Edge:</Typography>
            <Link
              component="a"
              target="_blank"
              href="https://support.microsoft.com/pl-pl/windows/windows-mikrofon-i-prywatno%C5%9B%C4%87-a83257bc-e990-d54a-d212-b5e41beba857"
            >
              Komputer
            </Link>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          disabled={isRequestingPermissions}
          onClick={requestPermissions}
        >
          {isRequestingPermissions ? translateKey("hardcoded.zadanie_uprawnien") : translateKey("hardcoded.zezwol_na_dostep")}
        </Button>
        {permissionDenied && (
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
          >{translateKey("hardcoded.odswiez_strone")}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
